import {randomKey} from 'common/src/utils/commonUtils';
import signal from 'common/src/utils/pubsub/signal';
import Subject from 'common/src/utils/pubsub/subject';
import {ZodTypeAny} from 'zod';
import CLLocalStorage from '../../localStorage/CLLocalStorage';
import {LS_KEY__CLINCH_COPILOT_CONTAINER_SETTINGS} from '../../localStorage/CLLocalStorage.consts';
import {CLRef} from '../shared.types';
import {UIToolsKey} from './bootstrap/bootstrap.enums';
import {copilotContainerSettingsDefaults} from './copilot.config';
import {AgentName} from './copilot.enums';
import {Agent, Chat, CopilotContainerSettings, CopilotFile, Event, Tool} from './copilot.types';

// trying to restrict access to these "private" variables
const allAgents: Agent[] = [];
const allChats: CLRef<Chat[]> = {current: []};
const eventsLog: CLRef<Event[]> = {current: []};
const maxEventsLogSize = 2000;

// tools that are dynamically added/removed depending on mounted/unmounted react components. Ideally, there would be no UI tools - tools would only be defined on the agents and work directly through redux.
const __DEPRECATED_uiTools: Partial<Record<UIToolsKey, Tool<ZodTypeAny>[]>> = {};

// The copilot container settings: coordinates, dimensions, dropzone etc
const copilotContainerSettingsSubject = new Subject<CopilotContainerSettings>(
  CLLocalStorage.getParsed(LS_KEY__CLINCH_COPILOT_CONTAINER_SETTINGS) ||
    copilotContainerSettingsDefaults,
);

function getAllAgents() {
  return [...allAgents];
}

// Perhaps this function should not be public -
// instead there should only be chat-level service functions for getting and setting chat-specific data
function getChatById(chatId: string): Chat | undefined {
  return allChats.current.find((chat) => chat.id === chatId);
}

function createChat() {
  const chat = _chat();
  allChats.current.push(chat);

  return chat;
}

function deleteChatById(chatId: string) {
  const chat = getChatById(chatId);

  if (!chat) {
    return;
  }

  chat.abortController?.abort();
  allChats.current = allChats.current.filter((chat) => chat.id !== chatId);
}

function registerAgent(agent: Agent) {
  if (allAgents.find((registeredAgent) => registeredAgent.name === agent.name)) {
    throw new Error(
      `Agent with name "${agent.name}" is already registered. Each agent must have a unique name.`,
    );
  }

  allAgents.push(agent);
}

function getFallbackAgent(): Agent {
  const fallbackAgent = allAgents.find((agent) => agent.name === AgentName.General);

  if (!fallbackAgent) {
    throw new Error('Fallback agent not found');
  }

  return fallbackAgent;
}

function clearChatById(chatId: string) {
  const chat = getChatById(chatId);

  if (!chat) {
    return;
  }

  const emptyChat = _chat();

  // preserve the chat ID, clear everything else.
  emptyChat.id = chatId;
  allChats.current = allChats.current.map((chat) => (chat.id === chatId ? emptyChat : chat));

  // Clear event log for this chat
  _clearEventsLogByChatId(chatId);
}

function addFilesToChat(chatId: string, newFiles: CopilotFile[]) {
  const chat = getChatById(chatId);

  if (chat) {
    chat.files = [...chat.files, ...newFiles];
  }
}

function _chat(): Chat {
  const id = randomKey();

  return {
    id,
    agentSession: null,
    blackboard: {
      chatHistory: [],
      plan: null,
    },
    abortController: null,
    draftFiles: [],
    files: [],
    currentInteractionId: null,
    cancellationCompleteSignal: signal(`copilot-cancellation-complete-${id}`),
    blockingWidgetResolvers: null,
  };
}

function addToEventsLog(event: Event) {
  eventsLog.current.push(event);
  const excessCount = eventsLog.current.length - maxEventsLogSize;
  eventsLog.current.splice(0, excessCount);
}

function getEventsLog() {
  return [...eventsLog.current];
}

function _clearEventsLogByChatId(chatId: string) {
  eventsLog.current = eventsLog.current.filter((event) => event.chatId !== chatId);
}

export {
  __DEPRECATED_uiTools,
  addFilesToChat,
  addToEventsLog,
  allChats,
  clearChatById,
  copilotContainerSettingsSubject,
  createChat,
  deleteChatById,
  getAllAgents,
  getChatById,
  getEventsLog,
  getFallbackAgent,
  registerAgent,
};
